import { requireRows, type SupabaseDataProvider } from './data-provider';

// DM-IMP-006 parent-student connection bridge (202609040006). Parent-facing
// redemption of a teacher-issued code, plus the confirm/decline/disconnect
// lifecycle and the two scoped read models. See student-connection-codes-data.ts
// for the teacher-facing issuance half.

export type DeniedResult = { ok: false; denialCode: string };

function isDenialRow(
  row: Record<string, unknown> | undefined,
): row is { denial_code: string } & Record<string, unknown> {
  return Boolean(row) && typeof row?.denial_code === 'string';
}

export type HomeSchoolConnectionStatus =
  | 'pending_teacher_confirmation'
  | 'connected'
  | 'declined_by_teacher'
  | 'disconnected';

export type RedeemedConnection = {
  connectionId: string;
  status: HomeSchoolConnectionStatus;
  schoolDisplayName: string;
};

export type RedeemConnectionCodeResult =
  { ok: true; data: RedeemedConnection } | DeniedResult;

function mapRedeemed(row: Record<string, unknown>): RedeemedConnection {
  if (
    typeof row.connection_id !== 'string' ||
    typeof row.status !== 'string' ||
    typeof row.school_display_name !== 'string'
  )
    throw new Error('INVALID_RESPONSE');
  return {
    connectionId: row.connection_id,
    status: row.status as HomeSchoolConnectionStatus,
    schoolDisplayName: row.school_display_name,
  };
}

export type StatusChangeResult =
  { ok: true; status: HomeSchoolConnectionStatus } | DeniedResult;

function mapStatusChange(row: Record<string, unknown>): StatusChangeResult {
  if (isDenialRow(row)) return { ok: false, denialCode: row.denial_code };
  if (typeof row.status !== 'string') throw new Error('INVALID_RESPONSE');
  return { ok: true, status: row.status as HomeSchoolConnectionStatus };
}

export type ParentConnection = {
  connectionId: string;
  homeLearnerSpaceId: string;
  homeSpaceAliasLabel: string;
  schoolDisplayName: string;
  status: HomeSchoolConnectionStatus;
  createdAt: string;
};

function mapParentConnection(row: Record<string, unknown>): ParentConnection {
  if (
    typeof row.connection_id !== 'string' ||
    typeof row.home_learner_space_id !== 'string' ||
    typeof row.home_space_alias_label !== 'string' ||
    typeof row.school_display_name !== 'string' ||
    typeof row.status !== 'string' ||
    typeof row.created_at !== 'string'
  )
    throw new Error('INVALID_RESPONSE');
  return {
    connectionId: row.connection_id,
    homeLearnerSpaceId: row.home_learner_space_id,
    homeSpaceAliasLabel: row.home_space_alias_label,
    schoolDisplayName: row.school_display_name,
    status: row.status as HomeSchoolConnectionStatus,
    createdAt: row.created_at,
  };
}

export type TeacherConnection = {
  connectionId: string;
  teacherStudentId: string;
  teacherStudentOrdinalLabel: string;
  status: HomeSchoolConnectionStatus;
  createdAt: string;
  // The parent's sign-in email, so the teacher knows who is asking
  // (2026-10-01). Null on a server that doesn't send it yet.
  parentEmail: string | null;
};

function mapTeacherConnection(row: Record<string, unknown>): TeacherConnection {
  if (
    typeof row.connection_id !== 'string' ||
    typeof row.teacher_student_id !== 'string' ||
    typeof row.teacher_student_ordinal_label !== 'string' ||
    typeof row.status !== 'string' ||
    typeof row.created_at !== 'string'
  )
    throw new Error('INVALID_RESPONSE');
  return {
    connectionId: row.connection_id,
    teacherStudentId: row.teacher_student_id,
    teacherStudentOrdinalLabel: row.teacher_student_ordinal_label,
    status: row.status as HomeSchoolConnectionStatus,
    createdAt: row.created_at,
    parentEmail: typeof row.parent_email === 'string' ? row.parent_email : null,
  };
}

export function createHomeSchoolConnectionService(
  provider: SupabaseDataProvider,
) {
  return {
    async redeemConnectionCode(
      homeLearnerSpaceId: string,
      code: string,
    ): Promise<RedeemConnectionCodeResult> {
      const result = await provider.rpc('parent_redeem_connection_code', {
        p_home_learner_space_id: homeLearnerSpaceId,
        p_code: code,
      });
      const row = requireRows(result)[0];
      if (!row) throw new Error('INVALID_RESPONSE');
      if (isDenialRow(row)) return { ok: false, denialCode: row.denial_code };
      return { ok: true, data: mapRedeemed(row) };
    },

    async confirmConnection(
      connectionId: string,
      approve: boolean,
    ): Promise<StatusChangeResult> {
      const result = await provider.rpc('teacher_confirm_home_connection', {
        p_connection_id: connectionId,
        p_approve: approve,
      });
      const row = requireRows(result)[0];
      if (!row) throw new Error('INVALID_RESPONSE');
      return mapStatusChange(row);
    },

    async parentDisconnect(connectionId: string): Promise<StatusChangeResult> {
      const result = await provider.rpc('parent_disconnect_home_connection', {
        p_connection_id: connectionId,
      });
      const row = requireRows(result)[0];
      if (!row) throw new Error('INVALID_RESPONSE');
      return mapStatusChange(row);
    },

    async teacherDisconnect(connectionId: string): Promise<StatusChangeResult> {
      const result = await provider.rpc('teacher_disconnect_home_connection', {
        p_connection_id: connectionId,
      });
      const row = requireRows(result)[0];
      if (!row) throw new Error('INVALID_RESPONSE');
      return mapStatusChange(row);
    },

    async listParentConnections(): Promise<ParentConnection[]> {
      const result = await provider.rpc(
        'parent_list_home_school_connections',
        {},
      );
      return requireRows(result).map(mapParentConnection);
    },

    async listTeacherConnections(): Promise<TeacherConnection[]> {
      const result = await provider.rpc(
        'teacher_list_home_school_connections',
        {},
      );
      return requireRows(result).map(mapTeacherConnection);
    },
  };
}

export type HomeSchoolConnectionService = ReturnType<
  typeof createHomeSchoolConnectionService
>;
